import React, {
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
} from "react";
import { Button } from "@shared/ui/button";
import {
  ChevronRight,
  ArrowLeft,
  Zap,
  Users,
  AlertTriangle,
  FileText,
  Shield,
} from "lucide-react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  useSafetyPackageOptions,
  useSafetyProtocolOptions,
  useSafetyEquipmentOptions,
} from "../../../hooks";
import {
  useSafetyPackages,
  useSafetyProtocols,
  usePPEEquipment,
} from "@features/wx/settings/hooks";
import { useUpdateSop } from "../../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useToast } from "@shared/hooks/use-toast";

// Type for safety package to handle both API formats
interface SafetyPackageItem {
  id: number;
  name: string;
  description?: string;
  // Handle both camelCase (after transform) and snake_case (direct API)
  safetyProtocol?: number[];
  safety_protocol?: number[];
  safetyEquipment?: number[];
  safety_equipment?: number[];
  isActive?: boolean;
  // From the transformed data (names)
  protocols?: string[];
  equipment?: string[];
}

interface SopSafetyStepProps {
  state: any;
  onUpdateState: (updates: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  readOnly?: boolean;
  templateId: string;
  onStepSave?: (stepIndex: number, data: any, isDirty: boolean) => void;
}

// Card component for safety package selection
const SafetyPackageCard: React.FC<{
  package: SafetyPackageItem;
  isSelected: boolean;
  onSelect: (packageId: string) => void;
  disabled?: boolean;
}> = ({ package: pkg, isSelected, onSelect, disabled = false }) => {
  const getPackageIcon = (name: string) => {
    const lowerName = name.toLowerCase();
    if (lowerName.includes("electrical")) return Zap;
    if (lowerName.includes("confined") || lowerName.includes("space"))
      return Users;
    if (lowerName.includes("hazardous") || lowerName.includes("materials"))
      return AlertTriangle;
    if (lowerName.includes("height") || lowerName.includes("elevated"))
      return FileText;
    return Shield; // Default for standard field package
  };

  const IconComponent = getPackageIcon(pkg.name);

  return (
    <div
      className={`
        relative p-4 rounded-lg border-2 cursor-pointer transition-all duration-200
        ${
          isSelected
            ? "border-orange-400 bg-orange-50 shadow-md"
            : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
        }
        ${disabled ? "opacity-50 cursor-not-allowed" : ""}
      `}
      onClick={() => !disabled && onSelect(pkg.id.toString())}
    >
      <div className="flex items-start space-x-3">
        <div
          className={`
          p-2 rounded-lg flex-shrink-0
          ${isSelected ? "bg-orange-100" : "bg-gray-100"}
        `}
        >
          <IconComponent
            className={`h-6 w-6 ${
              isSelected ? "text-orange-600" : "text-gray-600"
            }`}
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3
            className={`
            font-semibold text-sm mb-1
            ${isSelected ? "text-orange-900" : "text-gray-900"}
          `}
          >
            {pkg.name}
          </h3>
          <p
            className={`
            text-xs leading-relaxed
            ${isSelected ? "text-orange-700" : "text-gray-600"}
          `}
          >
            {pkg.description || "Safety package for specific work requirements"}
          </p>
        </div>
      </div>
    </div>
  );
};

const SopSafetyStep: React.FC<SopSafetyStepProps> = ({
  state,
  onUpdateState,
  onNext,
  onPrevious,
  readOnly = false,
  templateId,
  onStepSave,
}) => {
  const [selectedPackageData, setSelectedPackageData] =
    useState<SafetyPackageItem | null>(null);
  const hasPatchedRef = useRef(false);

  // Fetch dynamic data using hooks
  const { options: packageOptions, isLoading: loadingPackages } =
    useSafetyPackageOptions();
  const { data: packagesData } = useSafetyPackages(); // Get raw data for auto-population
  const { options: protocolOptions, isLoading: loadingProtocols } =
    useSafetyProtocolOptions();
  const { options: equipmentOptions, isLoading: loadingEquipment } =
    useSafetyEquipmentOptions();

  // Get the raw lookup data for name-to-ID mapping
  const { data: safetyProtocolsLookup = [] } = useSafetyProtocols();
  const { data: ppeEquipmentLookup = [] } = usePPEEquipment();

  // Convert packageOptions to cards data
  const packageCards = useMemo(() => {
    if (!packagesData || !Array.isArray(packagesData)) return [];
    return packagesData.filter((pkg) => pkg.isActive !== false);
  }, [packagesData]);

  // Define form fields using FormField system (excluding the dropdown)
  const safetyFormFields: FormField[] = useMemo(
    () => [
      {
        name: "safetyProtocols",
        label: "Safety Protocols",
        type: "multi-select",
        options: protocolOptions,
        required: !(state.safety?.safetyPackage || selectedPackageData && Object.keys(selectedPackageData).length),
        helperText:
          "Select the required safety protocols for this service order",
        showHelperTooltip: true,
        tooltipText:
          "Choose all safety protocols that must be followed when executing this SOP. These are mandatory safety steps.",
        disabled: readOnly || loadingProtocols,
        maxDisplayItems: 4,
        searchable: true,
        clearable: true,
        classes: {
          container: "w-full mb-8 border-t pt-8",
          label: "block text-xl font-semibold text-gray-700 mb-4",
          select: `w-full border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors ${
            readOnly || loadingProtocols
              ? "bg-gray-50 cursor-not-allowed"
              : "bg-white"
          }`,
          error: "text-red-600 text-sm mt-1",
          helperText: "text-sm text-gray-500 mb-5",
        },
        fullWidth: true,
        defaultValue: state.safety?.safetyProtocols || [],
      },
      {
        name: "ppe",
        label: "Personal Protective Equipment",
        type: "multi-select",
        options: equipmentOptions,
        required: !(state.safety?.safetyPackage || selectedPackageData && Object.keys(selectedPackageData).length),
        helperText: "Select the required PPE for this service order",
        showHelperTooltip: true,
        tooltipText:
          "Choose all Personal Protective Equipment that workers must wear when performing this SOP.",
        disabled: readOnly || loadingEquipment,
        maxDisplayItems: 3,
        searchable: false,
        clearable: true,
        classes: {
          container: "w-full mb-8",
          label: "block text-xl font-semibold text-gray-700 mb-4",
          select: `w-full border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors ${
            readOnly || loadingEquipment
              ? "bg-gray-50 cursor-not-allowed"
              : "bg-white"
          }`,
          error: "text-red-600 text-sm mt-1",
          helperText: "text-sm text-gray-500 mb-5",
        },
        fullWidth: true,
        defaultValue: state.safety?.ppe || [],
      },
    ],
    [
      readOnly,
      protocolOptions,
      equipmentOptions,
      loadingProtocols,
      loadingEquipment,
      state.safety?.safetyProtocols,
      state.safety?.ppe,
      selectedPackageData
    ]
  );

  // Create form using FormService instead of direct useForm
  const safetyForm = FormService.createForm({
    fields: safetyFormFields,
    mode: "onSubmit",
    validateOnMount: false,
  });

  // Patch existing values when returning to this step
  useEffect(() => {
    if (
      state.safety &&
      Object.keys(state.safety).length > 0 &&
      !safetyForm.formState.isDirty &&
      !hasPatchedRef.current
    ) {
      safetyForm.reset({
        safetyProtocols: state.safety.safetyProtocols || [],
        ppe: state.safety.ppe || [],
        fieldRealities: state.safety.fieldRealities || "",
      });

      // Prefer selectedPackageId if present, else safetyPackage
      const packageId =
        state.safety.selectedPackageId?.toString() ||
        state.safety.safetyPackage ||
        "";

      if (packageId && packagesData) {
        const packages = Array.isArray(packagesData) ? packagesData : [];
        const packageData = packages.find(
          (pkg) => pkg.id.toString() === packageId
        );
        if (packageData) {
          setSelectedPackageData(packageData);
          // Also update state.safety.safetyPackage if needed
          if (state.safety.safetyPackage !== packageId) {
            onUpdateState({
              safety: {
                ...state.safety,
                safetyPackage: packageId,
              },
            });
          }
        }
      }
      hasPatchedRef.current = true;
    }
  }, [
    state.safety,
    safetyForm.formState.isDirty,
    packagesData,
    safetyForm,
    onUpdateState,
  ]);

  // Debug: log state and packagesData
  useEffect(() => {}, [state.safety, packagesData]);

  // Reset patch flag when packagesData changes so patching effect can run again
  useEffect(() => {
    hasPatchedRef.current = false;
  }, [packagesData]);

  const utilityId = useRemoteUtilityId();
  const { toast } = useToast();
  const updateSopMutation = useUpdateSop();

  const handleFormSubmit = useCallback(
    async (data: any) => {
      // ✅ Add validation trigger at the start of submit
      const isValid = await safetyForm.trigger();
      if (!isValid) {
        console.log("VALIDATION REQUIRED");
        return;
      }
      // Check for SOP ID
      const sopId = state.basicInfo?.sopId || templateId;
      if (!sopId) {
        toast({
          title: "Error",
          description:
            "SOP ID is missing. Please complete the Basic Information step first.",
          variant: "destructive",
          duration: 5000,
        });
        return;
      }
      if (!utilityId) {
        toast({
          title: "Error",
          description: "Utility ID is missing.",
          variant: "destructive",
          duration: 5000,
        });
        return;
      }
      try {
        // Build payload as requested
        const safetyPackage =
          state.safety?.safetyPackage || data.safetyPackage || "";
        const safetyProtocols =
          data.safetyProtocols || state.safety?.safetyProtocols || [];
        const ppe = data.ppe || state.safety?.ppe || [];
        const apiData = {
          safety_requirements_updated: true,
          safety_requirements: {
            package: safetyPackage,
            safety_protocol: safetyProtocols,
            safety_equipment: ppe,
          },
          remoteUtilityId: parseInt(utilityId || "0"),
        };
        // Call the API
        await updateSopMutation.mutateAsync({
          sopId: sopId,
          data: apiData,
        });
        toast({
          title: "Safety Requirements Updated",
          description: "Safety requirements have been updated for this SOP.",
          duration: 3000,
        });
        // Update local state
        onUpdateState({
          safety: {
            ...state.safety,
            ...data,
          },
        });
        if (onStepSave) {
          await onStepSave(
            1,
            {
              // step index 1 for safety
              ...state.safety,
              ...data,
            },
            true
          );
        }
        if (onNext) onNext();
      } catch (error: any) {
        toast({
          title: "Failed to Update Safety Requirements",
          description:
            error?.response?.data?.message ||
            error?.message ||
            "An error occurred while updating safety requirements.",
          variant: "destructive",
          duration: 5000,
        });
      }
    },
    [
      state,
      utilityId,
      updateSopMutation,
      onUpdateState,
      onNext,
      toast,
      onStepSave,
    ]
  );

  const handlePackageSelect = useCallback(
  (packageId: string) => {
    if (!packageId || !packagesData) {
      setSelectedPackageData(null);
      safetyForm.setValue("safetyProtocols", []);
      safetyForm.setValue("ppe", []);
      onUpdateState({
        safety: {
          ...state.safety,
          safetyPackage: "",
          safetyProtocols: [],
          ppe: [],
        },
      });
      return;
    }

    const packages = Array.isArray(packagesData) ? packagesData : [];
    const packageData = packages.find(
      (pkg) => pkg.id.toString() === packageId
    );

    if (packageData) {
      setSelectedPackageData(packageData);

      // ✅ Use IDs directly from the package — handle both camelCase and snake_case
      const protocolIds: number[] =
        packageData.safetyProtocol ??
        packageData.safety_protocol ??
        [];

      const equipmentIds: number[] =
        packageData.safetyEquipment ??
        packageData.safety_equipment ??
        [];

      safetyForm.setValue("safetyProtocols", protocolIds);
      safetyForm.setValue("ppe", equipmentIds);

      onUpdateState({
        safety: {
          ...state.safety,
          safetyPackage: packageId,
          safetyProtocols: protocolIds,
          ppe: equipmentIds,
        },
      });
    }
  },
  [state.safety, onUpdateState, packagesData, safetyForm]
);

  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      // For protocol and PPE changes, just update normally
      const updatedData = { ...state.safety, [fieldName]: value };
      onUpdateState({ safety: updatedData });
    },
    [state.safety, onUpdateState]
  );

  const handleClearAll = useCallback(() => {
    safetyForm.setValue("safetyProtocols", []);
    safetyForm.setValue("ppe", []);
    setSelectedPackageData(null);

    // Clear parent state
    onUpdateState({
      safety: {
        safetyPackage: "",
        safetyProtocols: [],
        ppe: [],
        fieldRealities: "",
      },
    });
  }, [safetyForm, onUpdateState]);

  const renderCustomFooter = useCallback(() => {
    if (readOnly) return null;

    return (
      <div className="space-y-4">
        {/* Loading indicators */}
        {(loadingPackages || loadingProtocols || loadingEquipment) && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span className="text-sm text-blue-800">
                Loading safety data...
              </span>
            </div>
          </div>
        )}

        <div className="pt-6 flex justify-between border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={onPrevious}
            className="flex items-center gap-2 px-6 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              className="h-8 px-3 text-xs hover:bg-gray-100"
            >
              Clear All
            </Button>
            <Button
              type="submit"
              disabled={loadingPackages || loadingProtocols || loadingEquipment}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }, [
    readOnly,
    onPrevious,
    handleClearAll,
    selectedPackageData,
    loadingPackages,
    loadingProtocols,
    loadingEquipment,
  ]);

  return (
    <div className="space-y-6">
      {/* Safety Package Cards Section */}
      <div className="w-full mb-8">
        <label className="block text-xl font-semibold text-gray-700 mb-4">
          Quick Selection - Safety Package
        </label>
        <p className="text-sm text-gray-500 mb-5">
          Apply standard safety packages to quickly set common safety
          requirements
        </p>

        {loadingPackages ? (
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span className="text-sm text-gray-600">Loading packages...</span>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {packageCards.map((pkg) => (
              <SafetyPackageCard
                key={pkg.id}
                package={pkg}
                isSelected={state.safety?.safetyPackage === pkg.id.toString()}
                onSelect={handlePackageSelect}
                disabled={readOnly || loadingPackages}
              />
            ))}
          </div>
        )}
      </div>

      {/* Form with Protocol and PPE Fields */}
      <DynamicForm
        fields={safetyFormFields}
        form={safetyForm}
        onSubmit={handleFormSubmit}
        onFieldChange={handleFieldChange}
        loading={loadingProtocols || loadingEquipment}
        config={{
          showSubmitButton: false,
          showResetButton: false,
          classes: {
            form: "space-y-6",
            button: "px-4 py-2 rounded-md font-medium transition-colors",
            buttonContainer: "flex justify-end gap-3 pt-6 border-gray-200 mt-8",
            input:
              "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
            textarea:
              "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
            select:
              "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
            label: "block text-sm font-medium text-gray-700 mb-1",
            error: "text-sm text-red-600 mt-1",
            container: "space-y-1",
          },
        }}
        gridColumns={1}
        responsiveColumns={{ sm: 1, md: 1 }}
        renderCustomFooter={renderCustomFooter}
        className="space-y-6"
      />
    </div>
  );
};

export default SopSafetyStep;
